import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Images } from "lucide-react";
import { fadeUp, stagger } from "@/data/landingData";
import ProjectLightbox from "@/components/ProjectLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const fullUrl = (u) => (u && u.startsWith("/") ? `${backendOrigin}${u}` : u);

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        // Featured first, then the rest. Cap to 8 tiles on homepage.
        const sorted = [...list].sort(
          (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0),
        );
        setItems(sorted.slice(0, 8));
      })
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  return (
    <section
      id="portfolio"
      className="py-24 lg:py-32 section-surface"
      data-testid="portfolio"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
        >
          <div>
            <motion.span
              variants={fadeUp}
              className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]"
            >
              Portfólio
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="text-4xl sm:text-5xl font-black tracking-tight mt-4"
            >
              Naše <span className="text-[#00E5FF]">realizácie</span>
            </motion.h2>
          </div>
          <motion.div variants={fadeUp} className="mt-6 lg:mt-0">
            <Link
              to="/projekty"
              className="text-zinc-400 hover:text-[#00E5FF] transition-colors flex items-center gap-2"
              data-testid="portfolio-all-projects"
            >
              Všetky projekty <ArrowRight size={16} />
            </Link>
          </motion.div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((item, i) => {
            const cover = item.image || item.gallery?.[0]?.url;
            const count = item.gallery?.length || 0;
            return (
              <motion.button
                type="button"
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={() => setActive(item)}
                className="group relative aspect-[4/5] overflow-hidden rounded-2xl cursor-pointer text-left"
                data-testid={`portfolio-${item.id}`}
              >
                {cover ? (
                  <img
                    src={fullUrl(cover)}
                    alt={item.location ? `${item.title} — ${item.location}` : item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/placeholder-project.svg";
                    }}
                  />
                ) : (
                  <img
                    src="/placeholder-project.svg"
                    alt={item.location ? `${item.title} — ${item.location}` : item.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
                <div className="absolute inset-0 bg-[#00E5FF]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="mono text-xs text-[#00E5FF] uppercase tracking-wider">
                    {item.category}
                  </span>
                  <h3 className="text-xl font-bold mt-1">{item.title}</h3>
                </div>

                {count > 1 && (
                  <span className="absolute top-4 left-4 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-[11px] mono font-bold px-2.5 py-1 rounded-full">
                    <Images size={11} /> {count}
                  </span>
                )}

                <div className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-100 scale-50">
                  <ArrowUpRight size={18} />
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <ProjectLightbox project={active} onClose={() => setActive(null)} />
    </section>
  );
}
